import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import CafeCard from "@/components/CafeCard";
import useDocumentMeta from "@/hooks/useDocumentMeta";
import { ArrowLeft } from "lucide-react";

export default function ListDetailPage() {
  const { slug } = useParams();
  const [list, setList] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    setLoading(true);
    api.get(`/lists/${slug}`)
      .then(({ data }) => setList(data))
      .catch((e) => setError(e.response?.data?.detail || "List not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  useDocumentMeta({
    title: list?.title,
    description: list?.intro,
  });

  if (loading) {
    return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
      <div className="h-72 bg-card border border-border rounded-sm animate-pulse" />
    </div>;
  }
  if (error || !list) {
    return (
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-center" data-testid="list-not-found">
        <p className="font-serif text-3xl">{error || "List not found"}</p>
        <Link to="/lists" className="text-[#C05A46] mt-4 inline-block">← Back to lists</Link>
      </div>
    );
  }

  return (
    <div data-testid="list-detail-page" className="pb-24">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-6">
        <Link to="/lists" className="text-xs uppercase tracking-wider text-[#786B63] hover:text-[#C05A46] inline-flex items-center gap-1">
          <ArrowLeft size={14} strokeWidth={1.5} /> All lists
        </Link>
      </div>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-6">
        <div className="border border-border bg-card rounded-sm overflow-hidden">
          {list.hero_image_url && (
            <div className="aspect-[16/7] bg-[#E8DFD3] overflow-hidden">
              <img src={list.hero_image_url} alt="" className="w-full h-full object-cover" />
            </div>
          )}
          <div className="p-8 md:p-12">
            <p className="text-xs uppercase tracking-[0.25em] text-[#786B63]">
              {(list.cafes || []).length} cafés · by {list.author_name}
            </p>
            <h1 className="font-serif text-5xl sm:text-6xl mt-3 leading-[1.05]">{list.title}</h1>
            <p className="mt-5 text-lg text-[#2C1E16]/85 max-w-3xl leading-relaxed">{list.intro}</p>
          </div>
        </div>
      </section>

      {/* Numbered cafés */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10 space-y-10">
        {(list.cafes || []).map((c, i) => (
          <div key={c.id} data-testid={`list-item-${i + 1}`} className="grid md:grid-cols-[80px_1fr] gap-6 items-start">
            <div className="font-serif text-7xl text-[#C05A46] leading-none">{String(i + 1).padStart(2, "0")}</div>
            <CafeCard cafe={c} />
          </div>
        ))}
      </section>
    </div>
  );
}
